// The api_keys.scope values the console can mint — the SAME set the server
// accepts, in the SAME order. (OF-167)
//
// This used to be a hand-written seven-scope array in ApiKeyManager.tsx
// (validate, read, write, portal, admin, billing, scim) of which only `validate`
// and `admin` exist. The server refused the rest, and the scopes operators
// actually mint — telemetry, reports, reports_read, signup, internal — were not
// offered. One key does one job (D-50), so this is a single-select, not a bag.
//
// THREE COPIES OF ONE SET, FAIL-THE-BUILD TESTS BETWEEN THEM:
//   backend/src/types/index.ts           API_KEY_SCOPES  (feeds the mint schema)
//   supabase/migrations/ (the NEWEST one that defines api_keys_scope_check; 45 today)
//   this file                            the picker
// src/test/apiKeyScopes.contract.test.ts reads the other two and compares. To add
// a scope: a NEW migration widening the CHECK additively, the backend tuple, and
// a row here — in one PR.

export interface ApiKeyScopeInfo {
  value: string;
  /** One line shown under the picker when this scope is selected. */
  description: string;
  /** apikey.routes.ts superRefine: a `validate` key must be bound to a license. */
  needsLicense: boolean;
}

export const API_KEY_SCOPES: readonly ApiKeyScopeInfo[] = [
  {
    value: 'validate',
    description: 'Install phone-home. Validates one license, so it is bound to a license you pick below.',
    needsLicense: true,
  },
  {
    value: 'admin',
    description: 'Platform-wide service key: reaches everything a validate key can, for every license. Mint sparingly.',
    needsLicense: false,
  },
  {
    value: 'internal',
    description: "Platform-wide service key for Polestead's own systems, with the same reach as admin on key-authenticated routes.",
    needsLicense: false,
  },
  {
    value: 'telemetry',
    description: "The hosted TPRM7 plane's trust-rollup key: posts Trust Center metrics and contact syncs.",
    needsLicense: false,
  },
  {
    value: 'signup',
    description: 'The self-serve signup bridge: registers a new hosted org at account creation.',
    needsLicense: false,
  },
  {
    value: 'reports',
    description: 'The hosted bug-report bridge: files reports. Write-only.',
    needsLicense: false,
  },
  {
    value: 'reports_read',
    description: 'Agent diagnostics: reads one report and its recording by id. Read-only.',
    needsLicense: false,
  },
];

export const API_KEY_SCOPE_VALUES: readonly string[] = API_KEY_SCOPES.map((s) => s.value);

export function scopeInfo(value: string): ApiKeyScopeInfo | undefined {
  return API_KEY_SCOPES.find((s) => s.value === value);
}
